/* eslint-disable security/detect-object-injection */
import {Box, Typography} from '@mui/material';
import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import './DocView.scss';
import CodingForm, {CodingFormPinLabel} from './panels/coding-form/CodingForm';
import DocAnalytics from './panels/doc-analytics/DocAnalytics';
import DocDetails from './panels/doc-details/DocDetails';
import {
  clearDocumentCodeSameAsToastState,
  CodeSameAsType,
  defaultHeights,
  initHeights,
  normalizeHeights,
  Panel,
  removeCodeSameAsDocuments,
  resetDocAnalyticsState,
  resetReviewerRemarksPanel,
  setActiveDocument,
  setCheckedDocuments,
  setCodeSameAsDocuments,
  setDocumentCodeSameAsToastState,
  setDocumentViewLoaded,
  setRedactionPropagationAction,
  storeDocIdInHistory,
  storeDocViewConfig,
  triggerCodeSameAsRendering,
  triggerRemoveCodeSameAsRendering,
} from './docViewerUtils';
import {useVerticalResize} from './hooks/useVerticalResize';
import {useHorizontalResize} from './hooks/useHorizontalResize';
import MiniDocList from './panels/mini-doclist/MiniDocList';
import {VerticalPanelResize} from './panels/VerticalPanelResize';
import {HorizontalColumnResize} from './panels/HorizontalColumnResize';
import EditPanelDrawer from './panels/mini-doclist/edit-field/EditPanelDrawer';
import {useColumnResizeObserver} from './hooks/useColumnResizeObserver';
import {ExpandBanner} from './ExpandBanner';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  BgNavigationTypes,
  RoleId,
  SourceTypeCode,
  ToastType,
} from '../../shared/constants';
import {
  DocExplorerMasterRequest,
  DocViewRequest,
} from '../../api/types/document.type';
import {
  useCreateDocView,
  useCreateMasterDocList,
  useDocumentCompleteCheck,
  useGetReductionPropagation,
  useProgressBar,
  useRefreshAssignmentProgress,
} from '../../hooks/document/useDocument';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {debounce} from 'lodash';
import DraggablePanel from './panels/DraggablePanel';
import DocumentCenterPanel from './panels/document-center-panel/DocumentCenterPanel';
import {PortalPopoutManager} from '../PortalPopout';
import {MiniDocRowSelection} from './doc-list/DocViewDocListDataGrid';
import {
  clearCodingFormToastState,
  clearWarningToastState,
  defaultSaveCodingFormResponse,
  setAssignmentMode,
  setAssignmentProgressEnabled,
  setCodingFormToastState,
  setDocAnalyticsCheckedDocsConceptuallySimilar,
  setDocAnalyticsCheckedDocsFamilyMembers,
  setDocAnalyticsCheckedDocsNearDupe,
  setDocAnalyticsCheckedDocsThreadMap,
  setIsDocumentCompleted,
  setPanels,
  setSaveCodingFormResponse,
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../redux/slice/docViewSlice';
import ArrTooltip from '../common/arr-tooltip/ArrTooltip';
import {
  subscribeToGroup,
  unsubscribeFromGroup,
} from '../../redux/middleware/signalrMiddleware';
import {
  exportDocumentHistory,
  getDocIsCompleted,
} from '../../api/endpoints/documentApi';
import {DocCompletedCheckResponse} from '../../shared/interfaces';
import {
  setDocListBackNavigation,
  setDocListId,
  setPrevDocumentListMasterId,
} from '../../redux/slice/docListSlice';
import {useNavigate} from 'react-router-dom';
import ArrDocMaxLimitWarning from '../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import ActionLikeDocuments, {
  IActionListItem,
} from '../action-menu/action-like-documents/ActionLikeDocuments';
import {
  useCallBulkActionsLikePopup,
  useCallBulkActionsPopup,
  useDoAction,
} from '../../hooks/assignments/useAssignments';
import {
  IBulkActionLikePopupRequest,
  IBulkActionLikePopupResponse,
  IBulkActionPopupResponse,
} from '../../api/types/assignment.type';
import BulkFolderUnFolder from '../action-menu/bulk-folder-unfolder/BulkFolderUnFolder';
import {ActionSelection} from '../doc-explorer/ViewDocExplorer';
import {
  ActiveFilter,
  filterSelectionType,
} from '../common/arr-filter-panel/filter-accordion/ArrFilterAccordion';
import {deepClone} from '@mui/x-data-grid/internals';
import ArrModal from '../common/arr-modal/ArrModal';
import ArrTabFocusProvider from '../common/arr-tab-focus/ArrTabFocusProvider';
import CodingFormWrapper from './panels/coding-form/CodingFormWrapper';

const PANEL_HEADER_HEIGHT = 40;
const DRAGBAR_HEIGHT = 8;
const STATE_UPDATE_DEBOUNCE = 1000;

interface HoverInsert {
  side: string | null;
  index: number | null;
}

export interface VerticalResizeType {
  active: boolean;
  side?: string;
  idx?: number;
  startY?: number;
  startHeights?: number[];
  containerElem?: HTMLDivElement | null;
}
enum MiniDocListMenuItemType {
  folder = '1',
  codeSameAs = '2',
  removeCodeSame = '3',
  editFields = '4',
}

enum AnalyticsMenuItemType {
  folder = '1',
  codeSameAs = '2',
  removeCodeSame = '3',
  doclist = '4',
  viewInDocument = '5',
}

const DocView = () => {
  const [openEditFieldDrawer, setOpenEditFieldDrawer] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState({
    name: '',
    progress: 0,
  });
  const [assignmentProgressHeight, setAssignmentProgressHeight] = useState(0);

  const [noCodingFormBoxHeight, setNoCodingFormBoxHeight] = useState(0);
  const noCodingFormBoxRef = useRef<HTMLDivElement | null>(null);
  const assignmentProgressRef = useRef<HTMLDivElement | null>(null);
  const bulkScopeRef = useRef<'analytics' | 'doclist'>('doclist');
  const [codingFormUpdated, setCodingFormUpdated] = useState(false);
  const [codingFormPinLabel, setCodingFormPinLabel] =
    useState<CodingFormPinLabel>(CodingFormPinLabel.default);

  const codingFormToastState = useSelector(
    (state: RootState) => state.docView.codingFormToastState
  );

  const codeSameAsToastState = useSelector(
    (state: RootState) => state.docView.codeSameAsToastState
  );
  const checkedDocs = useSelector(
    (state: RootState) => state.docView.checkedDocs
  );
  const {codeSameAsDocs} = useSelector((state: RootState) => state.docView);
  const {
    checkedDocsFamilymembers,
    checkedDocsThreadMap,
    checkedDocsNearDupe,
    checkedDocsConceptuallySimilar,
  } = useSelector((state: RootState) => state.docView.docAnalytics);

  const currentDocAnalyticsTab = useSelector(
    (state: RootState) => state.docView.docAnalytics.currentTab
  );
  const userId = useSelector(
    (state: RootState) => state.authenticatedUser.user.userId
  );
  const currentRoleId = useSelector(
    (state: RootState) => state.authenticatedUser.user.roleId
  );
  const {
    user: {projectId},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const annotationLayerId = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.annotationLayerId
  );

  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);
  const [navigationType, setNavigationType] = useState<SourceTypeCode>();
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>(['-1']);
  const [columnFilters, setColumnFilters] = useState<
    {columnName: string; filterValue: string}[]
  >([]);
  const [isLoadingDocView, setIsLoadingDocView] = useState<boolean>(false);

  const navigate = useNavigate();

  const {
    miniDocListView,
    isShowFoldersTab,
    assignmentProgress,
    analyticsPanelView,
    isShowEmailThreadMapTabAnalyticsPanel,
    isShowNearDuplicateTabAnalyticsPanel,
    metadataView,
    isShowConceputallySimilarTabAnalyticsPanel,
    docHistoryView,
    isShowFamilyMembersTabAnalyticsPanel,
    isAllowCodingOutsideReviewerBatch,
    allowFullDocList,
    allowFolderAction,
  } = useSelector((state: RootState) => state.docView.docViewConfiguration);

  const showDocDetailsPanel = useMemo(
    () => isShowFoldersTab || metadataView || docHistoryView,
    [metadataView, docHistoryView, isShowFoldersTab]
  );

  const showAnalyticsPanel = useMemo(
    () =>
      analyticsPanelView &&
      (isShowEmailThreadMapTabAnalyticsPanel ||
        isShowNearDuplicateTabAnalyticsPanel ||
        isShowConceputallySimilarTabAnalyticsPanel ||
        isShowFamilyMembersTabAnalyticsPanel),
    [
      analyticsPanelView,
      isShowFamilyMembersTabAnalyticsPanel,
      isShowConceputallySimilarTabAnalyticsPanel,
      isShowEmailThreadMapTabAnalyticsPanel,
      isShowNearDuplicateTabAnalyticsPanel,
    ]
  );

  const codingFormDisabled = useMemo(
    () => Number(currentRoleId) === Number(RoleId.ProjectAdministrator),
    [currentRoleId]
  );

  const {isPinCodingFormSelected, codingFormList} = useSelector(
    (state: RootState) => state.docView
  );
  const [showExportToast, setShowExportToast] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (projectId && annotationLayerId) {
      dispatch(subscribeToGroup(`${projectId}-${annotationLayerId}`));
      return () => {
        dispatch(unsubscribeFromGroup(`${projectId}-${annotationLayerId}`));
      };
    }
  }, [annotationLayerId, projectId]);
  const [miniDocKey, setMiniDocKey] = useState<number>(Date.now());
  const [docAnalyticsKey, setDocAnalyticsKey] = useState<number>(Date.now());

  function handleOpenChange(arg0: boolean): void {
    setOpenEditFieldDrawer(arg0);
  }

  useEffect(() => {
    if (!noCodingFormBoxRef.current) return;
    const el = noCodingFormBoxRef.current;
    const observer = new ResizeObserver(() => {
      setNoCodingFormBoxHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!assignmentProgressRef.current) return;
    const el = assignmentProgressRef.current;
    const observer = new ResizeObserver(() => {
      setAssignmentProgressHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearCodingFormToastState());
    };
  }, []);

  const defaultPanels = useMemo(() => {
    const json = localStorage.getItem(`${userId}-panels`);
    return json
      ? JSON.parse(json)
      : {
          left: ['miniDocList', 'docAnalytics'],
          right: ['codingForm', 'docDetails'],
        };
  }, [userId]);

  const isPanelEnabled = useCallback(
    (id: string) => {
      switch (id) {
        case 'miniDocList':
          return !!miniDocListView;
        case 'docAnalytics':
          return !!showAnalyticsPanel;
        case 'docDetails':
          return !!showDocDetailsPanel;
        default:
          return true;
      }
    },
    [miniDocListView, showAnalyticsPanel, showDocDetailsPanel]
  );

  const filterPaneBasedOnPermissions = useCallback(
    (panels: string[]) => {
      return panels.filter((p) => isPanelEnabled(p));
    },
    [isPanelEnabled]
  );

  const [isDefaultPageLoad, setIsDefaultPageLoad] = useState(true);

  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  const [leftPanels, setLeftPanels] = useState<string[]>(
    filterPaneBasedOnPermissions(defaultPanels.left)
  );
  const [rightPanels, setRightPanels] = useState<string[]>(
    filterPaneBasedOnPermissions(defaultPanels.right)
  );

  useEffect(() => {
    setLeftPanels(filterPaneBasedOnPermissions(defaultPanels.left));
    setRightPanels(filterPaneBasedOnPermissions(defaultPanels.right));
  }, [filterPaneBasedOnPermissions]);

  const {data: getReductionPropagation} = useGetReductionPropagation();

  const sourceTypeCode = useSelector(
    (state: RootState) => state.docView.sourceTypeCode
  );
  const sourceId = useSelector((state: RootState) => state.docView.sourceId);

  const assignmentSource = useSelector(
    (state: RootState) => state.docView.assignmentSource
  );
  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);

  const warningToastState = useSelector(
    (state: RootState) => state.docView.warningToastState
  );

  useEffect(() => {
    const isAssignmentMode = sourceTypeCode === 'ASSIGNMENT';
    dispatch(setAssignmentMode(isAssignmentMode));
  }, [sourceTypeCode, dispatch]);

  useEffect(() => {
    dispatch(setSaveCodingFormResponse(defaultSaveCodingFormResponse));
  }, [dispatch]);

  useEffect(() => {
    if (activeDoc?.isCompleted !== undefined) {
      dispatch(setIsDocumentCompleted(Boolean(activeDoc.isCompleted)));
    }
  }, [activeDoc?.documentId, activeDoc?.isCompleted, dispatch]);

  useEffect(() => {
    if (activeDoc) {
      const isCompleted = Boolean(Number(activeDoc.isCompleted));
      dispatch(setIsDocumentCompleted(isCompleted));
      setCodingFormUpdated(false);
    }
  }, [activeDoc?.documentId, activeDoc?.isCompleted, dispatch]);

  useEffect(() => {
    setRedactionPropagationAction(getReductionPropagation || false);
  }, [getReductionPropagation]);

  const {mutate: createMasterList} = useCreateMasterDocList((response) => {
    dispatch(storeDocumentListMasterId(response.doclistId));
    dispatch(storeSourceTypeCode(SourceTypeCode.DOCUMENT));

    if (selectedActionId === 'ViewInDocList') {
      dispatch(setDocListId(Number(response.doclistId)));
    }
  });
  const [masterDocListId, setMasterDocListId] = useState<number>(0);
  const {mutate: callBulkActionsPopup, isPending: isBulkActionsPopupLoading} =
    useCallBulkActionsPopup(
      (resp: IBulkActionPopupResponse) => {
        setMasterDocListId(resp?.masterDocListID);
      },

      () => {
        triggerToast(
          t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError'),
          [ToastType.Error]
        );
      }
    );

  const {mutate: callBulkActionsLikePopup} = useCallBulkActionsLikePopup(
    (resp: IBulkActionLikePopupResponse) => {
      setMasterDocListId(resp?.masterDocListID);
      setAnalyticsIndicatorStatus(resp?.analyticsIndicatorStatus);
    },

    () => {
      triggerToast(
        t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError'),
        [ToastType.Error]
      );
    }
  );

  const {mutate: doAction} = useDoAction(
    () => {
      triggerToast(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess'),
        [ToastType.Success]
      );

      setIsActionLikeModal(false);

      setIsBulkFolderAction(false);

      setAnalyticsIndicatorStatus(0);
    },

    () => {
      triggerToast(
        t('docExplorer.actionMenu.bulkTaggingWarning.doActionError'),
        [ToastType.Error]
      );
    }
  );

  const getSelectedDocIdsForBulk = (scope: 'analytics' | 'doclist') => {
    const rows =
      scope === 'analytics' ? getDocAnalyticsSelectedDocuments : checkedDocs;

    return (rows ?? []).map((d: any) => String(d.documentId)).filter(Boolean);
  };

  const getBulkSelectedDocIds = () => {
    return getSelectedDocIdsForBulk(bulkScopeRef.current);
  };

  const buildBulkSourceDetails = (selectedDocIds: string[]) =>
    JSON.stringify({
      SelectedNodeIDs: selectedNodeIds.join(','),
      Filters: [],
      ColumnFilters: columnFilters,
      IsInverse: false,
      SelectedDocIds: selectedDocIds.join(','),
      ExcludedDocIds: '',
    });

  const prepareActiveFilterPayload = (filtersData: ActiveFilter[]) => {
    return filtersData.map((item) => {
      const {id, label, selectionType, values, type, endDate, startDate} = item;
      let operand: filterSelectionType;
      if (selectionType === 'include') {
        operand = 'IN';
      } else if (selectionType === 'exclude') {
        operand = 'NOTIN';
      } else {
        operand = selectionType || 'IN';
      }
      return {
        ProjectFieldId: id,
        ProjectFieldName: label,
        operand,
        values:
          type != 'DATETIME'
            ? values?.map((i) => i?.text)
            : [startDate, endDate],
        type,
      };
    });
  };

  const onBulkFolderSelect = (scope: 'analytics' | 'doclist') => {
    bulkScopeRef.current = scope;
    const selectedDocIds = getSelectedDocIdsForBulk(scope);

    if (!selectedDocIds.length) {
      return triggerToast(t('common.selectAtLeastOneDocument'), [
        ToastType.Warning,
      ]);
    }

    const commonPayload = {
      sourceType: SourceTypeCode.DOCVIEW,
      actionType: 'FOLDER',
      elementID: 'divBulkAction',
      selectedHitsDetails: [],
      sourceDetails: JSON.stringify({
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: prepareActiveFilterPayload(deepClone([])),
        ColumnFilters: columnFilters,
        IsInverse: false,
        SelectedDocIDs: selectedDocIds.join(','),
        ExcludedDocIds: '',
      }),
    };

    const callBulkActionsPopupPayload = {
      ...commonPayload,
      sourceTypeContext: SourceTypeCode.DOCEXPLORER,
      destinationTypeContext: '',
      documentCount: '',
      documentListMasterID: 0,
    };

    callBulkActionsPopup(callBulkActionsPopupPayload, {
      onSuccess: () => setIsBulkFolderAction(true),
    });
  };

  const callBulkActionsLikePopupApiCall = (
    bulkActionTreeIDs: string[],

    masterDocListIdToUse?: number,

    onSuccess?: () => void
  ) => {
    const likePopupPayload: IBulkActionLikePopupRequest = {
      sourceType: SourceTypeCode.DOCVIEW,
      actionType: 'FOLDER',
      elementID: 'divBulkAction',
      sourceTypeContext: '',
      destinationTypeContext: '',
      selectedHitsDetails: [],
      documentCount: '0',
      masterDocListID: masterDocListIdToUse ?? masterDocListId,
      isAdd: true,
      documentListMasterID: 0,
      docAssign: 0,
      sourceDetails: buildBulkSourceDetails(getBulkSelectedDocIds()),
      selectedDetinationIds: JSON.stringify({
        BulkActionTreeIDs: bulkActionTreeIDs.map(Number),
      }),
    };

    callBulkActionsLikePopup(likePopupPayload, {onSuccess});
  };

  const doFolderAction = (
    bulkActionTreeIDs: string[],
    selectedAction: 'folderDocs' | 'unfolderDocs'
  ) => {
    const selectedDocIds = getBulkSelectedDocIds();

    const isAdd = selectedAction === 'folderDocs';

    const doActionPayload = {
      sourceType: SourceTypeCode.DOCVIEW,
      actionType: 'FOLDER',
      elementId: 'divBulkAction',
      selectedList: {
        SelectedNodeIDs: selectedNodeIds.join(','),
        Filters: [],
        ColumnFilters: columnFilters,
        IsInverse: false,
        SelectedDocIds: selectedDocIds.join(','),
        ExcludedDocIds: '',
      },
      destinationList: {
        BulkActionTreeIDs: bulkActionTreeIDs,
        IsAdd: isAdd,
        SampleMethod: 'STANDARD',
        NumberToAssign: '0',
        MasterDocListID: String(masterDocListId),
      },
      masterDocListID: masterDocListId,
      strSelections: null,
    };

    doAction(doActionPayload);
  };

  const handleBulkFolderContinue = async (selectedAction: string) => {
    setBulkSelectedAction(selectedAction);

    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingFolder?.ids;

      if (Array.isArray(ids)) return ids;

      if (typeof ids === 'string') return [ids];

      return [];
    })();

    if (selectedAction === 'folderDocs') {
      callBulkActionsLikePopupApiCall(bulkActionTreeIDs, undefined, () => {
        setIsActionLikeModal('bulkFolder');
      });
    } else {
      setIsBulkFolderAction(false);
      doFolderAction(bulkActionTreeIDs, 'unfolderDocs');
    }
  };

  const handleActionLikeFinalize = () => {
    if (isActionLikeModal !== 'bulkFolder') return;

    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingFolder?.ids;

      if (Array.isArray(ids)) return ids;

      if (typeof ids === 'string') return [ids];

      return [];
    })();

    doFolderAction(bulkActionTreeIDs, bulkSelectedAction as any);
    setIsActionLikeModal(false);
    setAnalyticsIndicatorStatus(0);
  };

  const initialActionList = [
    {
      id: 'family_members',
      label: t(`common.actionMenu.familyMembers`),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'email_docs',
      label: t(`common.actionMenu.emailThread`),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'near_duplicates',
      label: t(`common.actionMenu.nearDuplicates`),
      isLoading: false,
      count: 0,
      selected: false,
    },
  ];

  type PanelsPosition = {
    left: string[];
    right: string[];
  };
  function rebuildSide(
    storedSide: string[],
    incomingEnabled: string[],
    isEnabled: (id: string) => boolean
  ): string[] {
    const result: string[] = [];
    const enabledQueue = [...incomingEnabled];

    storedSide.forEach((id) => {
      if (!isEnabled(id)) {
        result.push(id);
      } else if (enabledQueue.length) {
        result.push(enabledQueue.shift()!);
      }
    });

    result.push(...enabledQueue);

    return result;
  }

  function rebuildPanelPositions(
    stored: PanelsPosition,
    leftPanels: string[],
    rightPanels: string[],
    isEnabled: (id: string) => boolean
  ): PanelsPosition {
    return {
      left: rebuildSide(stored.left, leftPanels, isEnabled),
      right: rebuildSide(stored.right, rightPanels, isEnabled),
    };
  }
  useEffect(() => {
    const existing = localStorage.getItem(`${userId}-panels`);
    localStorage.setItem(
      `${userId}-panels`,
      JSON.stringify(
        rebuildPanelPositions(
          existing ? JSON.parse(existing) : defaultPanels,
          leftPanels,
          rightPanels,
          isPanelEnabled
        )
      )
    );
  }, [leftPanels, rightPanels, userId]);
  const [codingFormExpandDisabled, setCodingFormExpandDisabled] =
    useState(false);

  const {mutate: createDocView, isPending: isLoading} = useCreateDocView(
    (data) => {
      storeDocViewConfig(data);
      dispatch(setAssignmentProgressEnabled(data?.assignmentProgress ?? false));
      setAssignmentDetails((prev) => ({
        ...prev,
        name: data?.assignmentName ?? '',
      }));
    }
  );
  const {mutate: createProgressBarRequest} = useProgressBar((data) => {
    setAssignmentDetails((prev) => ({
      ...prev,
      progress: data,
    }));
  });

  const {mutateAsync: refreshAssignmentProgressApi} =
    useRefreshAssignmentProgress();

  const shouldRefreshAssignmentProgress = useSelector(
    (state: RootState) => state.docView.shouldRefreshAssignmentProgress
  );

  const assignmentMode = useSelector(
    (state: RootState) => state.docView.assignmentMode
  );

  useEffect(() => {
    if (shouldRefreshAssignmentProgress > 0 && assignmentMode) {
      const refreshProgress = async () => {
        await refreshAssignmentProgressApi({
          sourceID: sourceId ?? 0,
        });
        createProgressBarRequest({
          sourceId: sourceId ?? 0,
          sourceTypeCode: sourceTypeCode ?? 'DOCUMENT',
        });
      };

      refreshProgress();
    }
  }, [
    shouldRefreshAssignmentProgress,
    assignmentMode,
    sourceId,
    sourceTypeCode,
    createProgressBarRequest,
    refreshAssignmentProgressApi,
  ]);

  const {mutate: documentCompleteCheck} = useDocumentCompleteCheck();
  const [popoutsOpen, setPopoutsOpen] = useState<string[]>([]);
  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setDocumentViewLoaded(!isLoading);
    }, STATE_UPDATE_DEBOUNCE);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [isLoading]);

  const fixedPanels = useMemo(
    () => ({codingFormDisabled, assignmentMode}),
    [codingFormDisabled, assignmentMode]
  );
  const docList = useSelector((state: RootState) => state.docList);
  // cleanup useEffect
  useEffect(() => {
    // Register panel to store
    dispatch(
      setPanels({
        leftPanels,
        rightPanels,
        popoutsOpen,
        fixedPanels: Object.values(fixedPanels).some((p) => p),
      })
    );
    return () => {
      if (docList?.backSourceLabel === 'Doc View') return;
      setActiveDocument(null);
      setCheckedDocuments([]);
      setDocumentViewLoaded(false);
      setRedactionPropagationAction(false);
      resetReviewerRemarksPanel();
      resetDocAnalyticsState();
    };
  }, []);

  useEffect(() => {
    //this payload is used when the user redirects from DocExplorer to DocView.
    // If there are other redirection scenarios in the future, update the payload conditionally based on the respective source
    const payload: DocViewRequest = {
      sortconfiguration: {
        optimizedCols: '',
        optimizedSelectFieldIDs: '',
        optimizedSelectFieldOrder: '',
        manualCols: '',
        manualSelectFieldIDs: '',
        manualSelectFieldOrder: '',
        selectFields: '',
        configuredCols: '',
        order: '',
        showCompletedDocOptimized: true,
        showCompletedDocManual: true,
        configuredShowCompletedDoc: true,
        selectFieldTemp: '',
        selectFieldDataTypeOptimized: [],
        selectFieldDataType: [],
        selectFieldDataTypeManual: [],
        redirectionType: 0,
        manualOrder: '',
      },
      assignmentSortDetails: [],
      redirectMode: 0,
      documentListMasterID: 0,
      sourceTypeCode: sourceTypeCode ?? 'DOCUMENT',
      sourceID: sourceId ?? 0,
      assignmentSource: assignmentSource ?? '',
      docViewHistoryList: [],
    };

    createDocView(payload);
  }, [createDocView, miniDocKey]);

  useEffect(() => {
    createProgressBarRequest({
      sourceId: sourceId ?? 0,
      sourceTypeCode: sourceTypeCode ?? 'DOCUMENT',
    });
  }, [createProgressBarRequest, sourceTypeCode, miniDocKey]);

  // left section heights
  const [leftHeights, setLeftHeights] = useState<number[]>(
    leftPanels.map(
      (panel) => defaultHeights.find((pan) => pan.id === panel)?.height ?? 0
    )
  );

  //right section heights
  const [rightHeights, setRightHeights] = useState<number[]>(
    rightPanels.map(
      (panel) => defaultHeights.find((pan) => pan.id === panel)?.height ?? 0
    )
  );
  // ref for panel resize withing a section
  const verticalResizeRef = useRef<VerticalResizeType>({
    active: false,
  });

  useVerticalResize(
    verticalResizeRef,
    setLeftHeights,
    setRightHeights,
    setCodingFormExpandDisabled
  );

  const isResizingRef = useRef(false);
  const sideBeingResizedRef = useRef<string | null>(null);

  // Get the visible, filtered panels
  const filteredPanels = useMemo(() => {
    const filtered: {[key: string]: string[]} = {left: [], right: []};

    for (const side of ['left', 'right']) {
      const sidePanels = side === 'left' ? leftPanels : rightPanels;
      filtered[side] = sidePanels
        .filter((p) => (codingFormDisabled ? p !== 'codingForm' : true))
        .filter((p) => !popoutsOpen.includes(p));
    }
    return filtered;
  }, [leftPanels, rightPanels, popoutsOpen]);

  //left, right and center section width percentage along with resize
  const {
    leftWidth,
    setLeftWidth,
    rightWidth,
    setRightWidth,
    centerWidth,
    collapsedSections,
    expandCollapseOnClick,
  } = useHorizontalResize(
    windowWidth,
    isResizingRef,
    sideBeingResizedRef,
    filteredPanels['left'].length,
    filteredPanels['right'].length
  );

  //refs for left and right sections
  const leftColRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const loggedOnUser = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {computedLeftWidth, computedRightWidth} = useColumnResizeObserver(
    leftColRef,
    rightColRef
  );
  const docListWidth = useMemo(() => {
    const insideLeftSection = leftPanels.includes('miniDocList');
    const width = insideLeftSection ? computedLeftWidth : computedRightWidth;
    return width;
  }, [computedLeftWidth, computedRightWidth, leftPanels]);

  const docAnalyticsWidth = useMemo(() => {
    const insideLeftSection = leftPanels.includes('docAnalytics');
    const width = insideLeftSection ? computedLeftWidth : computedRightWidth;
    return width;
  }, [computedLeftWidth, computedRightWidth, leftPanels]);

  const docListMenuOptions = React.useMemo(
    () =>
      [
        {
          id: MiniDocListMenuItemType.folder,
          label: t('docView.headers.docList.folderOption'),
          onClick: () => onBulkFolderSelect('doclist'),
          disabled: !checkedDocs?.length,
        },
        {
          id: MiniDocListMenuItemType.codeSameAs,
          label: t('docView.headers.docList.codeSameAs'),
          onClick: () => {
            triggerCodeSameAsRendering({isTriggered: true});
          },
          disabled: !checkedDocs?.length,
        },
        {
          id: MiniDocListMenuItemType.removeCodeSame,
          label: t('docView.headers.docList.removeCodeSame'),
          onClick: () => {
            triggerRemoveCodeSameAsRendering({isTriggered: true});
          },
          disabled: !checkedDocs?.length,
        },
        {
          id: MiniDocListMenuItemType.editFields,
          label: t('docView.headers.docList.editFields'),
          onClick: () => {
            setOpenEditFieldDrawer(true);
          },
        },
      ].filter((option) =>
        allowFolderAction
          ? option
          : option.id !== MiniDocListMenuItemType.folder
      ),
    [checkedDocs, allowFolderAction]
  );

  const checkMoreOptionsDisabled = () => {
    if (currentDocAnalyticsTab === '') {
      return true;
    } else if (currentDocAnalyticsTab === 'thread-map-tab') {
      return !checkedDocsThreadMap?.length;
    } else if (currentDocAnalyticsTab === 'family-members-tab') {
      return !checkedDocsFamilymembers.length;
    } else if (currentDocAnalyticsTab === 'near-dupe-tab') {
      return !checkedDocsNearDupe.length;
    } else if (currentDocAnalyticsTab === 'conceptually-similar-tab') {
      return !checkedDocsConceptuallySimilar.length;
    }
  };

  const setEmptyDocAnalyticsTab = () => {
    if (currentDocAnalyticsTab == 'family-members-tab') {
      dispatch(setDocAnalyticsCheckedDocsFamilyMembers([]));
    } else if (currentDocAnalyticsTab == 'near-dupe-tab') {
      dispatch(setDocAnalyticsCheckedDocsNearDupe([]));
    } else if (currentDocAnalyticsTab == 'conceptually-similar-tab') {
      dispatch(setDocAnalyticsCheckedDocsConceptuallySimilar([]));
    } else {
      dispatch(setDocAnalyticsCheckedDocsThreadMap([]));
    }
  };

  const handleCodeSameAs = async (codeSameAsType: CodeSameAsType) => {
    let selectedDocs;

    if (currentDocAnalyticsTab === 'thread-map-tab') {
      selectedDocs = checkedDocsThreadMap;
    } else if (currentDocAnalyticsTab === 'family-members-tab') {
      selectedDocs = checkedDocsFamilymembers;
    } else if (currentDocAnalyticsTab === 'near-dupe-tab') {
      selectedDocs = checkedDocsNearDupe;
    } else {
      selectedDocs = checkedDocsConceptuallySimilar;
    }
    if (!isAllowCodingOutsideReviewerBatch) {
      let docsStatus;
      try {
        docsStatus = (await getDocIsCompleted({
          assignmentSource: '',
          sourceID: Number(sourceId),
          sourceTypeCode: 'ASSIGNMENT',
          selectedDocs: selectedDocs.map((doc) => `${doc.documentId}`),
        })) as DocCompletedCheckResponse;
      } catch (error) {
        console.error(error);
      }
      if (docsStatus?.docNotInAssignment != '') {
        triggerToast(t('docView.codeSameNotAllowed'), [ToastType.Error]);
        return;
      }
    }
    const payload = {
      assignmentSource: '',
      sourceID: sourceId,
      sourceTypeCode: 'DOCUMENT',
      selectedDocs: selectedDocs.map((doc) => doc.documentId.toString()),
    };
    const codeSameAsPayload: MiniDocRowSelection[] = selectedDocs.map(
      (doc) => ({
        documentId: doc.documentId.toString(),
        docId: doc.docId,
        rowNumber: doc?.rowNumber ?? 0,
        fileName: doc.fileName,
      })
    );
    if (codeSameAsType === CodeSameAsType.remove) {
      removeCodeSameAsDocuments(codeSameAsPayload);

      setTimeout(() => {
        setEmptyDocAnalyticsTab();
      }, 0);

      setDocAnalyticsKey(Date.now());
      const codeSameAsDocIds = new Set(
        codeSameAsDocs.map((item) => item.docId)
      );
      const showWarning = selectedDocs.some(
        (item) => !codeSameAsDocIds.has(item.docId)
      );
      if (showWarning) {
        setDocumentCodeSameAsToastState({
          showToast: true,
          toastMessage: t(
            'docView.codeSameAs.removeCodeSameAsAnalyticsWarning'
          ),
          toastType: ToastType.Warning,
        });
      } else {
        setDocumentCodeSameAsToastState({
          showToast: true,
          toastMessage: t(
            'docView.codeSameAs.removeCodeSameAsAnalyticsSuccess'
          ),
          toastType: ToastType.Success,
        });
      }
      return;
    }
    documentCompleteCheck(payload, {
      onSuccess: (data) => {
        if (
          checkedDocsThreadMap.some((doc) => doc.isCompleted) ||
          data?.docsCompleted
        ) {
          setDocumentCodeSameAsToastState({
            showToast: true,
            toastMessage: t('docView.codeSameAs.codeSameAsWaring'),
            toastType: ToastType.Warning,
          });
          triggerCodeSameAsRendering({isTriggered: false});
        } else {
          setDocumentCodeSameAsToastState({
            showToast: true,
            toastMessage: t('docView.codeSameAs.codeSameAsSuccess'),
            toastType: ToastType.Success,
          });

          setTimeout(() => {
            setEmptyDocAnalyticsTab();
          }, 0);

          setCodeSameAsDocuments(codeSameAsPayload);
          setDocAnalyticsKey(Date.now());
        }
      },
      onError: (error) => {
        setDocumentCodeSameAsToastState({
          showToast: true,
          toastMessage: error?.message,
          toastType: ToastType.Error,
        });
      },
    });
  };

  const exportHistory = async (documentId: number | string = 0) => {
    if (documentId) {
      try {
        const {status} = await exportDocumentHistory(documentId);

        if (status === 200) {
          setShowExportToast(true);
          setTimeout(() => {
            setShowExportToast(false);
          }, 5000);
        }
      } catch (error) {
        console.error('History Export Failed!', error);
      }
    }
  };

  const filterAnalyticsOptions = useCallback(
    (op: {id: string}) => {
      return (
        (loggedOnUser.roleId == Number(RoleId.ProjectAdministrator)
          ? op.id !== AnalyticsMenuItemType.codeSameAs &&
            op.id !== AnalyticsMenuItemType.removeCodeSame
          : true) &&
        (!allowFullDocList ? op.id !== AnalyticsMenuItemType.doclist : true) &&
        (!allowFolderAction ? op.id !== AnalyticsMenuItemType.folder : true)
      );
    },
    [loggedOnUser, allowFolderAction, allowFullDocList]
  );
  const [showPopupForRoute, setShowPopupForRoute] = useState<string>();

  const PanelConfig: Panel[] = useMemo(
    () => [
      {
        id: 'miniDocList',
        title: t('docView.headers.docList.title'),
        component: ({width, height, panelId}) => {
          return (
            <MiniDocList
              width={width === 0 ? docListWidth : width}
              height={height}
              key={miniDocKey}
              refreshKey={miniDocKey}
              codingFormUpdated={codingFormUpdated}
              panelId={panelId}
            />
          );
        },
        menuOptions:
          loggedOnUser.roleId == Number(RoleId.ProjectAdministrator)
            ? docListMenuOptions.filter(
                (option) =>
                  ![
                    MiniDocListMenuItemType.codeSameAs,
                    MiniDocListMenuItemType.removeCodeSame,
                  ].includes(option.id)
              )
            : docListMenuOptions,
      },
      {
        id: 'docAnalytics',
        title: t('docView.headers.analytics.title'),
        component: ({width, panelId}) => {
          return (
            <DocAnalytics
              key={docAnalyticsKey}
              width={width === 0 ? docAnalyticsWidth : width}
              panelId={panelId}
            />
          );
        },
        menuOptions: [
          {
            id: AnalyticsMenuItemType.folder,
            label: t('docView.headers.analytics.folderOption'),
            onClick: () => onBulkFolderSelect('analytics'),
            disabled: checkMoreOptionsDisabled(),
          },
          {
            id: AnalyticsMenuItemType.codeSameAs,
            label: t('docView.headers.analytics.codeSameAs'),
            onClick: () => {
              handleCodeSameAs(CodeSameAsType.add);
            },
            disabled: checkMoreOptionsDisabled(),
          },
          {
            id: AnalyticsMenuItemType.removeCodeSame,
            label: t('docView.headers.analytics.removeCodeSame'),
            onClick: () => {
              handleCodeSameAs(CodeSameAsType.remove);
            },
            disabled: checkMoreOptionsDisabled(),
          },
          {
            id: AnalyticsMenuItemType.doclist,
            label: t('docView.headers.analytics.viewInDocList'),
            onClick: () => {
              if (codingFormUpdated) {
                setShowPopupForRoute(AnalyticsMenuItemType.doclist);
              } else {
                handleViewInDocList();
              }
            },
            disabled: checkMoreOptionsDisabled(),
          },
          {
            id: AnalyticsMenuItemType.viewInDocument,
            label: t('docView.headers.analytics.viewInDocument'),
            onClick: () => {
              if (codingFormUpdated) {
                setShowPopupForRoute(AnalyticsMenuItemType.viewInDocument);
              } else {
                handleViewDocument();
              }
            },
            disabled: checkMoreOptionsDisabled(),
          },
        ].filter(filterAnalyticsOptions),
      },
      {
        id: 'codingForm',
        title: t('docView.headers.codingForm.title'),
        component: ({panelId}) => (
          <CodingForm
            panelId={panelId}
            codingFormPinLabel={codingFormPinLabel}
            setIsDirty={setCodingFormUpdated}
            onToastShow={(message: string, type: ToastType) => {
              dispatch(
                setCodingFormToastState({
                  showToast: true,
                  toastMessage: message,
                  toastType: type,
                })
              );
            }}
          />
        ),
        menuOptions:
          codingFormList?.length > 1
            ? [
                {
                  id: '1',
                  label: isPinCodingFormSelected
                    ? t('docView.headers.codingForm.unpinCF')
                    : t('docView.headers.codingForm.pinCF'),
                  onClick: () => {
                    if (isPinCodingFormSelected) {
                      setCodingFormPinLabel(CodingFormPinLabel.unpin);
                    } else if (codingFormPinLabel === CodingFormPinLabel.pin) {
                      setCodingFormPinLabel(CodingFormPinLabel.pinAgain);
                    } else {
                      setCodingFormPinLabel(CodingFormPinLabel.pin);
                    }
                  },
                },
                {
                  id: '2',
                  label: t('docView.headers.codingForm.fitToHeight'),
                  disabled: codingFormExpandDisabled,
                  onClick: () => {
                    const el = document.getElementById('rightColumns');
                    const containerTotalHeight = el
                      ? parseFloat(window.getComputedStyle(el).height)
                      : 0;

                    const isOnLeft = leftPanels.includes('codingForm');
                    const panelList = isOnLeft ? leftPanels : rightPanels;
                    const updateHeights = isOnLeft
                      ? setLeftHeights
                      : setRightHeights;

                    if (containerTotalHeight > 0) {
                      const minHeightPercentage =
                        (PANEL_HEADER_HEIGHT / containerTotalHeight) * 100;
                      const dragBarPercentage =
                        (DRAGBAR_HEIGHT / containerTotalHeight) * 100;

                      const noOfPanels = panelList.length;
                      const maxHeight =
                        100 -
                        (noOfPanels - 1) * minHeightPercentage -
                        (noOfPanels - 1) * dragBarPercentage;
                      const updatedHeights = panelList.map((panel) =>
                        panel === 'codingForm' ? maxHeight : minHeightPercentage
                      );
                      updateHeights(updatedHeights);
                      setCodingFormExpandDisabled(true);
                    }
                  },
                },
              ]
            : [
                {
                  id: '2',
                  label: t('docView.headers.codingForm.fitToHeight'),
                  disabled: codingFormExpandDisabled,
                  onClick: () => {
                    const el = document.getElementById('rightColumns');
                    const containerTotalHeight = el
                      ? parseFloat(window.getComputedStyle(el).height)
                      : 0;

                    const isOnLeft = leftPanels.includes('codingForm');
                    const panelList = isOnLeft ? leftPanels : rightPanels;
                    const updateHeights = isOnLeft
                      ? setLeftHeights
                      : setRightHeights;

                    if (containerTotalHeight > 0) {
                      const minHeightPercentage =
                        (PANEL_HEADER_HEIGHT / containerTotalHeight) * 100;
                      const dragBarPercentage =
                        (DRAGBAR_HEIGHT / containerTotalHeight) * 100;

                      const noOfPanels = panelList.length;
                      const maxHeight =
                        100 -
                        (noOfPanels - 1) * minHeightPercentage -
                        (noOfPanels - 1) * dragBarPercentage;
                      const updatedHeights = panelList.map((panel) =>
                        panel === 'codingForm' ? maxHeight : minHeightPercentage
                      );
                      updateHeights(updatedHeights);
                      setCodingFormExpandDisabled(true);
                    }
                  },
                },
              ],
      },
      {
        id: 'docDetails',
        title: t('docView.headers.docDetails.title'),
        component: ({panelId}) => (
          <DocDetails panelId={panelId} onHistoryExport={exportHistory} />
        ),
        menuOptions: [],
      },
    ],
    [
      codingFormUpdated,
      checkedDocsThreadMap,
      checkedDocsConceptuallySimilar,
      checkedDocsFamilymembers,
      checkedDocsNearDupe,
      miniDocKey,
      currentDocAnalyticsTab,
      codingFormExpandDisabled,
      docListMenuOptions,
      docListWidth,
      leftPanels,
      loggedOnUser.roleId,
      rightPanels,
      codingFormPinLabel,
      isPinCodingFormSelected,
      codingFormList,
      dispatch,
      filterAnalyticsOptions,
    ]
  );

  // Check the signalR message navigate accordingly
  useEffect(() => {
    if (!isNavigating) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        (navType === SourceTypeCode.DOCVIEW ||
          navType === SourceTypeCode.DOCLIST ||
          navType === SourceTypeCode.EXPORTDATA)
      ) {
        setIsLoadingDocView(false);
        setIsNavigating(false);
        navigateToDocPage();
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoadingDocView(false);
      setIsNavigating(false);
      setToastMessage(t('docExplorer.docDataGrid.navigationErrorToastText'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
      return;
    }
  }, [lastEvent, selectedActionId, docListMasterId]);

  const startDocumentNavigation = (
    actionId: string,
    totalDocumentToView: number
  ) => {
    const payload: DocExplorerMasterRequest = {
      docViewSessionObjects: {
        documentListMasterID: 0,
        sourceTypeCode: SourceTypeCode.DOCVIEW,
      },
      sourceInfo: {
        selectedNodeIDs: selectedNodeIds.join(','),
        filters: [],
        columnFilters,
        isInverse: false,
        selectedDocIDs: getDocAnalyticsSelectedDocuments
          ?.map((d) => d.documentId)
          .join(','),
        excludedDocIDs: '',
      },
      filterDetails: [],
      destinationTypeCode: SourceTypeCode.DOCLIST,
      documentCount: totalDocumentToView,
    };
    // Navigation actions
    setIsNavigating(true);
    setShowDocLimitWarning(false);
    setIsLoadingDocView(true);
    createMasterList(payload);
  };

  const getDocAnalyticsSelectedDocuments = useMemo(() => {
    let selectedDocument;

    switch (currentDocAnalyticsTab) {
      case 'thread-map-tab':
        selectedDocument = checkedDocsThreadMap;
        break;
      case 'family-members-tab':
        selectedDocument = checkedDocsFamilymembers;
        break;
      case 'near-dupe-tab':
        selectedDocument = checkedDocsNearDupe;
        break;
      case 'conceptually-similar-tab':
        selectedDocument = checkedDocsConceptuallySimilar;
        break;
      default:
        selectedDocument = checkedDocsThreadMap;
    }
    return selectedDocument;
  }, [
    currentDocAnalyticsTab,
    checkedDocsThreadMap,
    checkedDocsFamilymembers,
    checkedDocsNearDupe,
    checkedDocsConceptuallySimilar,
  ]);

  const handleViewInDocList = () => {
    dispatch(setPrevDocumentListMasterId(docListMasterId));
    setNavigationType(SourceTypeCode.DOCLIST);
    setSelectedActionId('ViewInDocList');
    setShowDocLimitWarning(true);
  };

  const navigateToDocPage = () => {
    setShowDocLimitWarning(false);
    const backPath = `${window.location.pathname}${window.location.search}`;
    dispatch(
      setDocListBackNavigation({
        label: t('docView.title'),
        path: backPath,
      })
    );
    navigate('/Document/DocList');
  };

  const handleViewDocument = () => {
    const selectedDocument = getDocAnalyticsSelectedDocuments;

    if (selectedDocument?.length > 1) {
      return triggerToast(
        t('docView.docAnalytics.viewDocumentMultiSelectError'),
        [ToastType.Warning]
      );
    } else if (selectedDocument?.length === 1) {
      const rowData = selectedDocument[0];

      const activeDocument = {
        documentId: rowData?.documentId,
        rowNumber: rowData?.rowNumber,
        isCompleted: rowData?.isCompleted,
        docId: rowData?.docId,
        fileName: rowData?.fileName,
        fileTypeCode: rowData?.fileTypeCode,
        miniMediaLength: rowData?.miniMediaLength,
        miniAudioTrimmedLength: rowData?.MiniAudioTrimmedLength,
        miniAudioDocDateTimeOnly: rowData?.MiniAudioDocDateTimeOnly,
        docExt: rowData?.MiniDocListDocExt as string,
        documentGUID: rowData?.DocumentGUID,
        ingestionGUID: rowData?.IngestionGUID,
        __InternalExcelProtectedSheets: rowData?.__InternalExcelProtectedSheets,
        __InternalExcelProtectedWorkbook:
          rowData?.__InternalExcelProtectedWorkbook,
        __InternalHiddenProperties: rowData?.__InternalHiddenProperties,
        __InternalHiddenPropertiesText: rowData?.__InternalHiddenPropertiesText,
        internalVideoPlayerReady: rowData?.__InternalVideoPlayerReady,
        ActualParameters: rowData?.ActualParameters,
        ConceptParameters: rowData?.ConceptParameters,
        MinConceptScore: rowData?.MinConceptScore,
      };
      setActiveDocument(activeDocument);
      storeDocIdInHistory(activeDocument);
    } else {
      return;
    }
  };

  // contains data regarding which panel is getting dragged and from which section
  const dragDataRef = useRef<{id: string; from: string} | null>(null);

  // Contains information regarding which side the panel is getting dropped and at which index it will be dropped
  const [hoverInsert, setHoverInsert] = useState<HoverInsert>({
    side: null,
    index: null,
  });

  // 'left' or 'right' when near outer edge
  const [hoverEdge, setHoverEdge] = useState<{side: string | null}>({
    side: null,
  });

  //id of pannel getting dragged
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

  // Bulk Folder (DocView)

  const [isBulkFolderAction, setIsBulkFolderAction] = useState(false);
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean | string>(
    false
  );
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');
  const [selectedNewExistingFolder, setSelectedNewExistingFolder] = useState<
    ActionSelection | null | undefined
  >();
  const [analyticsIndicatorStatus, setAnalyticsIndicatorStatus] =
    useState<number>(0);
  const [actionLikeChangeList, setActionLikeChangeList] =
    useState<IActionListItem[]>();

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  // resize left section panel heights if count of panel is changed
  useEffect(() => {
    setLeftHeights((prev) => {
      if (leftPanels.length === prev.length) return prev;
      if (isDefaultPageLoad) {
        return leftPanels.map(
          (panel) => defaultHeights.find((pan) => pan.id === panel)?.height ?? 0
        );
      }
      return normalizeHeights(initHeights(leftPanels.length));
    });
    if (leftPanels.length && isDefaultPageLoad) {
      setIsDefaultPageLoad(false);
    }
  }, [leftPanels.length, isDefaultPageLoad]);

  // resize right section panel heights if count of panel is changed
  useEffect(() => {
    setRightHeights((prev) => {
      if (rightPanels.length === prev.length) return prev;
      if (isDefaultPageLoad) {
        return rightPanels.map(
          (panel) => defaultHeights.find((pan) => pan.id === panel)?.height ?? 0
        );
      }
      return normalizeHeights(initHeights(rightPanels.length));
    });
    if (rightPanels.length && isDefaultPageLoad) {
      setIsDefaultPageLoad(false);
    }
  }, [rightPanels.length && isDefaultPageLoad]);

  // ----------------- drag & drop -----------------
  // Drag panel start
  const onDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, panelId: string, from: string) => {
      e.dataTransfer.setData('text/plain', panelId);
      dragDataRef.current = {id: panelId, from};
      setTimeout(() => setDraggingId(panelId), 0);
      e.dataTransfer.effectAllowed = 'move';
    },
    [setDraggingId]
  );
  const onDragEnd = useCallback(() => {
    dragDataRef.current = null;
    setDraggingId(null);
    setHoverInsert({side: null, index: null});
    setHoverEdge({side: null});
    setCodingFormExpandDisabled(false);
  }, [
    setDraggingId,
    setHoverEdge,
    setHoverInsert,
    setCodingFormExpandDisabled,
  ]);

  //Getting Called when dropping on Edge - when column is empty
  const handleEdgeDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, toSide: string) => {
      e.preventDefault();
      const panelId = e.dataTransfer?.getData('text/plain');
      if (!panelId || !dragDataRef.current) return;
      const from = dragDataRef.current.from;
      const panel =
        from === 'left'
          ? leftPanels.find((panel) => panel === panelId)
          : rightPanels.find((panel) => panel === panelId);
      if (!panel) return;

      // remove from source
      if (from === 'left')
        setLeftPanels((prev) => prev.filter((panel) => panel !== panelId));
      else setRightPanels((prev) => prev.filter((panel) => panel !== panelId));

      // compute width from clientX (option B behavior)
      if (toSide === 'left') {
        setLeftWidth(rightWidth);
        setLeftPanels((prev) => [...prev, panel]);
        setLeftHeights(
          normalizeHeights(
            initHeights(leftPanels.length - (from === 'left' ? 1 : 0) + 1)
          )
        );
      } else {
        setRightWidth(leftWidth);
        setRightPanels((prev) => [...prev, panel]);
        setRightHeights(
          normalizeHeights(
            initHeights(rightPanels.length - (from === 'right' ? 1 : 0) + 1)
          )
        );
      }

      onDragEnd();
    },
    [
      leftPanels,
      leftWidth,
      onDragEnd,
      rightPanels,
      rightWidth,
      setLeftWidth,
      setRightWidth,
    ]
  );

  // called when a dragged item is over left/right column
  const handleColumnDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, side: string) => {
      e.preventDefault();
      const columnRef =
        side === 'left' ? leftColRef.current : rightColRef.current;
      const heights = side === 'left' ? leftHeights : rightHeights;

      // If column is missing or empty -> show drop at start and edge highlight (for that specific column)
      // here highlighting is happening
      if (
        (!columnRef || filteredPanels[side].length === 0) &&
        (side === 'left' ? !assignmentMode : !codingFormDisabled)
      ) {
        setHoverInsert({side, index: 0});
        if (filteredPanels[side].length === 0) setHoverEdge({side});
        else setHoverEdge({side: null});
        return;
      }

      // otherwise clear any edge highlight ( keep in-column insertion logic)
      setHoverEdge({side: null});

      // compute insert index by mouse Y inside .col-content
      const content = columnRef?.querySelector('.col-content') || columnRef;
      const rect = content?.getBoundingClientRect() ?? {top: 0, height: 0};
      const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      const pct = (y / rect.height) * 100;
      if (!heights || heights.length === 0) {
        setHoverInsert({side, index: 0});
        return;
      }
      let cum = 0;
      // by default  add panel to the end
      let found = heights.length;
      for (let index = 0; index < heights.length; index++) {
        const mid = cum + heights[index] / 2;
        if (pct < mid) {
          // save index , if panel needs to be aded in middle
          found = index;
          break;
        }
        cum += heights[index];
      }
      setHoverInsert({side, index: found});
    },
    [leftHeights, rightHeights, filteredPanels]
  );

  // Drop panel handler - for columns as well as edge
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, side: string) => {
      e.preventDefault();

      const panelId = e.dataTransfer?.getData('text/plain');
      if (!panelId || !dragDataRef.current) return;
      const from = dragDataRef.current.from;

      // if hoverEdge is active (user dropping to outer edge), insert at end
      if (hoverEdge.side) {
        // if hoverEdge set by window-level detection and column is missing -> treat as edge drop
        handleEdgeDrop(e, hoverEdge.side);
        return;
      }

      // normal drop using hoverInsert (in-column)
      const destIndex =
        hoverInsert.index != null
          ? hoverInsert.index
          : side === 'left'
            ? leftPanels.length
            : rightPanels.length;

      if (from === side) {
        // reorder within same column
        if (side === 'left') {
          const arr = leftPanels.filter((panel) => panel !== panelId);
          const panel = leftPanels.find((panel) => panel === panelId);
          if (panel) {
            arr.splice(destIndex, 0, panel);
            setLeftPanels(arr);
            setLeftHeights(normalizeHeights(initHeights(arr.length)));
          }
        } else {
          const arr = rightPanels.filter((panel) => panel !== panelId);
          const panel = rightPanels.find((panel) => panel === panelId);
          if (panel) {
            arr.splice(destIndex, 0, panel);
            setRightPanels(arr);
            setRightHeights(normalizeHeights(initHeights(arr.length)));
          }
        }
        onDragEnd();
        return;
      }

      // move between columns - remove from source
      const panel =
        from === 'left'
          ? leftPanels.find((panel) => panel === panelId)
          : rightPanels.find((panel) => panel === panelId);
      if (!panel) return;

      if (from === 'left')
        setLeftPanels((prev) => prev.filter((panel) => panel !== panelId));
      else setRightPanels((prev) => prev.filter((panel) => panel !== panelId));

      if (side === 'left') {
        setLeftPanels((prev) => {
          const copy = [...prev];
          copy.splice(destIndex, 0, panel);
          return copy;
        });
        setLeftHeights(
          normalizeHeights(
            initHeights(leftPanels.length - (from === 'left' ? 1 : 0) + 1)
          )
        );
      } else {
        setRightPanels((prev) => {
          const copy = [...prev];
          copy.splice(destIndex, 0, panel);
          return copy;
        });
        setRightHeights(
          normalizeHeights(
            initHeights(rightPanels.length - (from === 'right' ? 1 : 0) + 1)
          )
        );
      }

      onDragEnd();
    },
    [
      handleEdgeDrop,
      hoverEdge.side,
      hoverInsert.index,
      leftPanels,
      onDragEnd,
      rightPanels,
    ]
  );

  // effective widths: if panels empty width = 0 so center expands
  const effectiveLeftWidth = useMemo(
    () => (filteredPanels['left'].length || assignmentMode ? leftWidth : 0),
    [filteredPanels, leftWidth, assignmentMode]
  );

  const effectiveRightWidth = useMemo(
    () =>
      filteredPanels['right'].length || codingFormDisabled ? rightWidth : 0,
    [filteredPanels, rightWidth, codingFormDisabled]
  );

  const effectiveCenterWidth = useMemo(() => {
    if (
      filteredPanels['left'].length === 0 &&
      !assignmentMode &&
      filteredPanels['right'].length === 0 &&
      !codingFormDisabled
    ) {
      return centerWidth + leftWidth + rightWidth;
    } else if (filteredPanels['left'].length === 0) {
      return centerWidth + leftWidth;
    } else if (filteredPanels['right'].length === 0) {
      return centerWidth + rightWidth;
    } else {
      return centerWidth;
    }
  }, [
    centerWidth,
    leftWidth,
    rightWidth,
    filteredPanels,
    assignmentMode,
    codingFormDisabled,
  ]);
  // --------------------------

  const handlePopouts = (id: string, isOpen: boolean = false) => {
    if (isOpen) {
      setPopoutsOpen((prev) => [...prev, id]);
    } else {
      setPopoutsOpen((prev) => prev.filter((p) => p !== id));
    }
  };
  // --------------------------

  useEffect(() => {
    // Update panels in store
    dispatch(
      setPanels({
        leftPanels,
        rightPanels,
        popoutsOpen,
        fixedPanels: Object.values(fixedPanels).some((p) => p),
      })
    );
  }, [popoutsOpen]);
  // --------------------------

  const shouldRenderResize = useCallback(
    ({panel, side}: {panel: string; side: string; index: number}) => {
      if (popoutsOpen.includes(panel)) return false;

      const sidePanels = side === 'left' ? leftPanels : rightPanels;
      const filtered = sidePanels
        .filter((p) => (codingFormDisabled ? p !== 'codingForm' : true))
        .filter((p) => !popoutsOpen.includes(p));
      const index = filtered.findIndex((p) => p === panel);

      return index < filtered.length - 1;
    },
    [leftPanels, popoutsOpen, rightPanels]
  );
  // --------------------------

  const showLeftCollapsedBanner = useMemo(() => {
    return (
      Boolean(
        (Boolean(leftPanels.length) && filteredPanels['left'].length) ||
        assignmentMode
      ) && Boolean(collapsedSections.includes('left'))
    );
  }, [leftPanels, filteredPanels, assignmentMode, collapsedSections]);

  const showRightCollapsedBanner = useMemo(() => {
    return (
      Boolean(
        (Boolean(rightPanels.length) && filteredPanels['right'].length) ||
        codingFormDisabled
      ) && Boolean(collapsedSections.includes('right'))
    );
  }, [rightPanels, filteredPanels, codingFormDisabled, collapsedSections]);

  const handleDragShorcut = (key: string, panelId: string, side: string) => {
    if (key === 'ArrowRight' && side === 'left') {
      const leftPanelItems = leftPanels.filter((el) => el !== panelId);
      const rightPanelItems = [...rightPanels, panelId];
      setLeftPanels(leftPanelItems);
      setRightPanels(rightPanelItems);
      return;
    }
    if (key === 'ArrowLeft' && side === 'right') {
      const rightPanelItems = rightPanels.filter((el) => el !== panelId);
      const leftPanelItems = [...leftPanels, panelId];
      setLeftPanels(leftPanelItems);
      setRightPanels(rightPanelItems);
      return;
    }

    if (key === 'ArrowUp') {
      const panelItems = side === 'left' ? [...leftPanels] : [...rightPanels];

      // Not the top item
      if (panelItems[0] !== panelId) {
        const itemIndex = panelItems.indexOf(panelId);
        // Swap items
        [panelItems[itemIndex], panelItems[itemIndex - 1]] = [
          panelItems[itemIndex - 1],
          panelItems[itemIndex],
        ];
        if (side === 'left') setLeftPanels(panelItems);
        else setRightPanels(panelItems);
      }
      return;
    }

    if (key === 'ArrowDown') {
      const panelItems = side === 'left' ? [...leftPanels] : [...rightPanels];

      // Not the last item
      if (panelItems.at(-1) !== panelId) {
        const itemIndex = panelItems.indexOf(panelId);
        // Swap items
        [panelItems[itemIndex], panelItems[itemIndex + 1]] = [
          panelItems[itemIndex + 1],
          panelItems[itemIndex],
        ];
        if (side === 'left') setLeftPanels(panelItems);
        else setRightPanels(panelItems);
      }
    }
  };

  const renderColumnPanels = useCallback(
    (side: string) => {
      const panels = (side === 'left' ? leftPanels : rightPanels).filter((p) =>
        codingFormDisabled ? p !== 'codingForm' : true
      );
      const heights = side === 'left' ? leftHeights : rightHeights;
      const items = [];
      const dragPanelIndex = panels.findIndex((p) => p === draggingId);
      const idx = hoverInsert.index;
      const show = hoverInsert.side === side && dragPanelIndex !== idx;
      if (idx === 0 && show) {
        items.push(
          <VerticalPanelResize
            side={side}
            index={-1}
            hoverIndex={idx}
            showHover={show}
            heights={heights}
            leftColRef={leftColRef}
            rightColRef={rightColRef}
            verticalResizeRef={verticalResizeRef}
            allowResize={false}
            data-tab-id={`v-handle-${side}-${-1}`}
            data-tab-group="doc-view"
          />
        );
      }

      for (let index = 0; index < panels.length; index++) {
        const panel = panels[index];
        const h = heights[index] ?? 100 / panels.length;

        if (panel.toLowerCase() === 'codingform') {
          items.push(
            <CodingFormWrapper>
              <DraggablePanel
                key={panel}
                panel={PanelConfig.find((p) => p.id === panel)}
                side={side}
                draggingId={draggingId}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                height={h}
                style={
                  collapsedSections.includes(side) ? {display: 'none'} : {}
                }
                onHeaderKeyDown={handleDragShorcut}
              />
            </CodingFormWrapper>
          );
        } else {
          items.push(
            <DraggablePanel
              key={panel}
              panel={PanelConfig.find((p) => p.id === panel)}
              side={side}
              draggingId={draggingId}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              height={h}
              style={collapsedSections.includes(side) ? {display: 'none'} : {}}
              onHeaderKeyDown={handleDragShorcut}
            />
          );
        }

        if (shouldRenderResize({panel, side, index})) {
          items.push(
            <VerticalPanelResize
              key={`resize-${index}`}
              side={side}
              index={index}
              hoverIndex={idx}
              showHover={show}
              heights={heights}
              leftColRef={leftColRef}
              rightColRef={rightColRef}
              verticalResizeRef={verticalResizeRef}
              allowResize
              data-tab-id={`v-handle-${side}-${index}`}
              data-tab-group="doc-view"
            />
          );
        }
      }
      if (
        panels.length
          ? idx === panels.length && show
          : side === 'left'
            ? !assignmentMode
            : !codingFormDisabled
      ) {
        items.push(
          <VerticalPanelResize
            side={side}
            index={panels.length - 1}
            hoverIndex={idx}
            showHover={show}
            heights={heights}
            leftColRef={leftColRef}
            rightColRef={rightColRef}
            verticalResizeRef={verticalResizeRef}
            allowResize={false}
            data-tab-id={`v-handle-${side}-${panels.length - 1}`}
            data-tab-group="doc-view"
          />
        );
      }
      return items;
    },
    [
      leftPanels,
      rightPanels,
      leftHeights,
      rightHeights,
      hoverInsert.index,
      hoverInsert.side,
      draggingId,
      PanelConfig,
      onDragStart,
      onDragEnd,
      collapsedSections,
      shouldRenderResize,
      assignmentMode,
      codingFormDisabled,
    ]
  );
  // --------------------------

  const getColumnPanels = (side: string) => {
    const panels = (side === 'left' ? leftPanels : rightPanels).filter((p) =>
      codingFormDisabled ? p !== 'codingForm' : true
    );
    const template = [];

    const fixedSectionsPresent =
      side === 'left' ? assignmentMode : codingFormDisabled;
    // Conditions
    const IS_PANEL_AVAILABLE = Boolean(panels.length) || fixedSectionsPresent;
    const IS_PANEL_VISIBLE = Boolean(
      (IS_PANEL_AVAILABLE && filteredPanels[side].length) ||
      fixedSectionsPresent
    );
    const IS_PANEL_COLLAPSED =
      IS_PANEL_VISIBLE && Boolean(collapsedSections.includes(side));

    if (IS_PANEL_AVAILABLE) {
      template.push(renderColumnPanels(side));
    }

    if (IS_PANEL_COLLAPSED) {
      template.push(
        <ExpandBanner
          className={`expand-${side}`}
          onExpandClick={() => expandCollapseOnClick([side], true)}
          dataTestId={`expand-${side}`}
        />
      );
    }

    if (!IS_PANEL_VISIBLE) {
      template.push(
        <>
          <Box
            data-testid={`unusedEdge-${side}`}
            className={`unused-edge-inactive ${side}  ${hoverEdge.side === side && 'unused-edge-active'}`}
          ></Box>
          {hoverEdge.side === side && (
            <Box className={`edge-indicator ${side}-edge`} />
          )}
        </>
      );
    }

    return template;
  };
  // --------------------------

  const showExpandSections = useMemo(
    () =>
      collapsedSections.includes('left') && collapsedSections.includes('right'),
    [collapsedSections]
  );

  const renderUnsavedCodingPopup = () => (
    <ArrModal
      showModal={!!showPopupForRoute}
      title={t('docView.headers.codingForm.loseChanges')}
      showClose
      onClose={() => {
        setShowPopupForRoute(undefined);
      }}
      dataTestId="render-unsaved-coding-modal"
      buttons={[
        {
          id: '1',
          label: t('docView.headers.codingForm.loseChangesCancel'),
          onClick: () => {
            setShowPopupForRoute(undefined);
          },
          variant: 'secondary',
          dataTestId: 'ruc-cancel-btn',
        },
        {
          id: '2',
          label: t('docView.headers.codingForm.loseChangesOK'),

          onClick: () => {
            if (showPopupForRoute === AnalyticsMenuItemType.doclist) {
              handleViewInDocList();
            } else if (
              showPopupForRoute === AnalyticsMenuItemType.viewInDocument
            ) {
              handleViewDocument();
            }
            setShowPopupForRoute(undefined);
          },
          variant: 'primary',
          dataTestId: 'ruc-ok-btn',
        },
      ]}
      description={t('docView.headers.codingForm.loseChangesOnDocSwitch')}
    ></ArrModal>
  );

  return (
    <ArrTabFocusProvider id="doc-view">
      <Box
        className={`doc-view-container ${popoutsOpen.length ? 'with-popouts' : ''}`}
      >
        {renderUnsavedCodingPopup()}
        <PortalPopoutManager
          defaultOptions={{
            windowFeatures: {
              width: 600,
              height: 800,
            },
          }}
          onPopoutOpen={(id) => {
            handlePopouts(id, true);
          }}
          onPopoutClose={(id) => {
            handlePopouts(id, false);
          }}
        >
          <Box className="layout">
            {/* LEFT COLUMN */}
            <Box
              className={`column left-column 
            ${filteredPanels['left'].length === 0 && !assignmentMode ? 'left-empty-panel' : ''} 
            ${collapsedSections.includes('left') ? 'collapsed' : ''}
          `}
              sx={
                collapsedSections.includes('left')
                  ? {display: 'none', width: `${effectiveLeftWidth}%`}
                  : {width: `${effectiveLeftWidth}%`}
              }
              onDragOver={(e) => handleColumnDragOver(e, 'left')}
              onDrop={(e) => handleDrop(e, 'left')}
              ref={leftColRef}
            >
              <Box className="fixed-section">
                {!showLeftCollapsedBanner && assignmentMode && (
                  <Box
                    ref={assignmentProgressRef}
                    className="assignment-section"
                    data-tab-id="assignment"
                    data-tab-group="doc-view"
                  >
                    <ArrTooltip content={assignmentDetails.name}>
                      <Box className="assignment-box">
                        <Typography
                          component={'span'}
                          className="typo-text subhead5_size"
                          data-testid={`doc-view-assignment-prefix`}
                        >
                          {t('docView.assignmentPrefix')}
                        </Typography>
                        <Typography
                          component={'span'}
                          className="typo-text body3_size assignment-name"
                          data-testid={`doc-view-assignment-name`}
                        >
                          {assignmentDetails.name}
                        </Typography>
                      </Box>
                    </ArrTooltip>
                    {assignmentProgress && (
                      <Box className="progress-box">
                        <Box
                          className="progress-value"
                          sx={{width: `${assignmentDetails.progress}%`}}
                        ></Box>
                      </Box>
                    )}
                  </Box>
                )}
                <Box
                  className="col-content"
                  sx={{
                    height: assignmentMode
                      ? `calc(100% - ${assignmentProgressHeight}px) !important`
                      : '100%',
                  }}
                >
                  {getColumnPanels('left')}
                </Box>
              </Box>
            </Box>

            {(filteredPanels['left'].length > 0 || assignmentMode) && (
              <HorizontalColumnResize
                side="left-center"
                isResizingRef={isResizingRef}
                sideBeingResizedRef={sideBeingResizedRef}
              />
            )}

            {/* CENTER */}
            <Box
              className="column center-column"
              style={{width: `${effectiveCenterWidth}%`}}
              data-tab-id="documentViewer"
              data-tab-group="doc-view"
            >
              <ArrTabFocusProvider id="center-panel">
                <DocumentCenterPanel
                  showExpandSections={showExpandSections}
                  expandCollapseOnClick={() => {
                    expandCollapseOnClick(
                      ['left', 'right'],
                      showExpandSections
                    );
                  }}
                  centerWidth={effectiveCenterWidth}
                  codingFormUpdated={codingFormUpdated}
                />
              </ArrTabFocusProvider>
            </Box>

            {(filteredPanels['right'].length > 0 || codingFormDisabled) && (
              <HorizontalColumnResize
                side="center-right"
                isResizingRef={isResizingRef}
                sideBeingResizedRef={sideBeingResizedRef}
              />
            )}

            {/* RIGHT COLUMN */}
            <Box
              id="rightColumns"
              className={`column right-column 
            ${filteredPanels['right'].length === 0 && !codingFormDisabled ? 'right-empty-panel' : ''} 
            ${collapsedSections.includes('right') ? 'collapsed' : ''}
          `}
              sx={
                collapsedSections.includes('right')
                  ? {display: 'none'}
                  : {width: `${effectiveRightWidth}%`}
              }
              onDragOver={(e) => handleColumnDragOver(e, 'right')}
              onDrop={(e) => handleDrop(e, 'right')}
              ref={rightColRef}
            >
              <Box className="fixed-section">
                {!showRightCollapsedBanner && codingFormDisabled && (
                  <Box className="no-coding-box" ref={noCodingFormBoxRef}>
                    <Typography
                      className="typo-text body3_size"
                      data-testid="no-coding-text"
                    >
                      {t('docView.noCodingForm')}
                    </Typography>
                  </Box>
                )}

                <Box
                  className="col-content"
                  sx={{
                    height: codingFormDisabled
                      ? `calc(100% - ${noCodingFormBoxHeight}px) !important`
                      : '100%',
                  }}
                >
                  {getColumnPanels('right')}
                  {openEditFieldDrawer && (
                    <EditPanelDrawer
                      showdrawer={openEditFieldDrawer}
                      onDrawerClose={handleOpenChange}
                      onSaveConfirm={() => {
                        setMiniDocKey(Date.now());
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {codeSameAsToastState?.showToast && (
            <ArrToastNotification
              message={codeSameAsToastState.toastMessage}
              onClose={() => {
                clearDocumentCodeSameAsToastState();
              }}
              status={[codeSameAsToastState.toastType]}
            />
          )}
          {codingFormToastState?.showToast && (
            <ArrToastNotification
              message={codingFormToastState.toastMessage}
              onClose={(id, message) => {
                dispatch(clearCodingFormToastState());
                if (
                  message ===
                  t('docView.headers.codingForm.toast.documentUncompleted')
                )
                  setMiniDocKey(Date.now());
              }}
              status={[codingFormToastState.toastType]}
            />
          )}
          {showToast && (
            <ArrToastNotification
              message={toastMessage}
              status={toastStatus}
              onClose={() => setShowToast(false)}
            />
          )}
        </PortalPopoutManager>

        {showExportToast && (
          <ArrToastNotification
            data-testid="history-export-success-toast"
            title={t('common.success')}
            message={t('docView.historyExportSuccessMessage')}
            status={[ToastType.Success]}
            onClose={() => setShowExportToast(false)}
          />
        )}
        {warningToastState?.showToast && (
          <ArrToastNotification
            data-testid="assignment-warning-toast"
            title={t('common.warning')}
            message={warningToastState.toastMessage}
            status={[warningToastState.toastType]}
            onClose={() => dispatch(clearWarningToastState())}
          />
        )}
        {isLoadingDocView && (
          <ArrLoadingSpinner overlay isOpen={isLoadingDocView} />
        )}
        <ArrDocMaxLimitWarning
          totalDocumentCount={getDocAnalyticsSelectedDocuments?.length || 0}
          show={showDocLimitWarning}
          destination={navigationType}
          onCancel={() => setShowDocLimitWarning(false)}
          onContinue={(totalDocumentToView: number) =>
            startDocumentNavigation(selectedActionId, totalDocumentToView)
          }
        />

        {isBulkFolderAction && (
          <BulkFolderUnFolder
            data-testid="bulk-folder"
            isOpen={isBulkFolderAction}
            setOpenActionMenu={setIsBulkFolderAction}
            handleBulkFolderContinue={handleBulkFolderContinue}
            masterDocListId={masterDocListId}
            setSelectedNewExistingFolder={setSelectedNewExistingFolder}
            isNonbg={false}
          />
        )}

        {isActionLikeModal === 'bulkFolder' && (
          <ActionLikeDocuments
            dataTestId="action-like"
            selectedIds={getBulkSelectedDocIds()}
            showModal
            onClose={() => {
              setIsActionLikeModal(false);
              setActionLikeChangeList(undefined);
              setAnalyticsIndicatorStatus(0);
              setIsBulkFolderAction(false);
            }}
            onCancel={() => {
              setIsActionLikeModal(false);
              setActionLikeChangeList(undefined);
              setAnalyticsIndicatorStatus(0);
              setIsBulkFolderAction(false);
            }}
            actionList={initialActionList}
            onContinue={handleActionLikeFinalize}
            continueButtonLabel={t(
              'docExplorer.actionMenu.actionLikeDocuments.finalize.bulkFolder'
            )}
            onActionChange={(_, __, updated) =>
              setActionLikeChangeList(updated)
            }
            masterDocListId={masterDocListId}
            analyticsIndicatorStatus={analyticsIndicatorStatus}
            actionType="FOLDER"
          />
        )}

        {isBulkActionsPopupLoading && (
          <ArrLoadingSpinner overlay isOpen={isBulkActionsPopupLoading} />
        )}
      </Box>
    </ArrTabFocusProvider>
  );
};

export default DocView;
